import { useMemo, useRef, useState } from "react";
import { GripVertical, Trash2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { HubWorkspaceBody, HubWorkspaceHeader, HubWorkspacePage } from "@/components/layout/HubWorkspace";
import { DeferredCommitTextInput } from "@/components/mrp/DeferredCommitTextInput";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { OperatorScriptStepsEditor } from "@/components/qc/OperatorScriptStepsEditor";
import { ApiError, apiFetch } from "@/lib/api";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";

type ScriptAnswerOutcome = "repair" | "scrap" | "samples_only" | "tier1";

type ScriptAnswer = {
  label: string;
  outcome: ScriptAnswerOutcome;
  followUp: string | null;
};

type OperatorScript = {
  id: number;
  label: string;
  question: string | null;
  steps: string;
  options: ScriptAnswer[];
};

type AnswerDraft = {
  key: string;
  label: string;
  followUp: string;
  outcome: ScriptAnswerOutcome;
  lockedTier1: boolean;
};

const NEXT_PAGE_BY_OUTCOME: Record<ScriptAnswerOutcome, string> = {
  repair: "The device is repaired and can continue production.",
  scrap: "Scrap the device and set it aside.",
  samples_only: "Set the device aside for samples only.",
  tier1: "Deliver the device to Tier 1 review.",
};

const ANSWER_ACTION_LABEL: Record<ScriptAnswerOutcome, string> = {
  repair: "Repair",
  scrap: "Scrap",
  samples_only: "Samples-only",
  tier1: "Tier 1",
};

const ANSWER_OUTCOMES: ScriptAnswerOutcome[] = ["repair", "scrap", "samples_only", "tier1"];

function answerActionLabel(outcome: ScriptAnswerOutcome): string {
  return `${ANSWER_ACTION_LABEL[outcome]} (${NEXT_PAGE_BY_OUTCOME[outcome]})`;
}

function answerDraft(
  key: string,
  label: string,
  outcome: ScriptAnswerOutcome,
  lockedTier1 = false,
): AnswerDraft {
  return { key, label, followUp: NEXT_PAGE_BY_OUTCOME[outcome], outcome, lockedTier1 };
}

function defaultAnswers(): AnswerDraft[] {
  return [answerDraft("yes", "Yes", "repair"), answerDraft("tier1", "No", "tier1", true)];
}

function stepLineCount(steps: string): number {
  return steps.split("\n").filter((line) => line.trim() !== "").length;
}

function answerCount(script: OperatorScript): number {
  const stored = script.options.length;
  const hasTier1 = script.options.some((option) => option.outcome === "tier1");
  return hasTier1 ? stored : stored + 1;
}

function answersFromScript(script: OperatorScript | undefined): AnswerDraft[] {
  if (script == null) return defaultAnswers();
  let locked = false;
  const stored = script.options.map((option, index) => {
    const lockedTier1 = option.outcome === "tier1" && !locked;
    if (lockedTier1) locked = true;
    return answerDraft(`${option.outcome}-${index}`, option.label, option.outcome, lockedTier1);
  });
  if (!locked) stored.push(answerDraft("tier1", stored.length === 0 ? "Done" : "No", "tier1", true));
  return stored;
}

function useOperatorScripts() {
  return useQuery({
    queryKey: ["quality", "failure-modes"],
    queryFn: () =>
      apiFetch<{
        operatorScripts: OperatorScript[];
        rows: { operatorScripts: { id: number }[] }[];
      }>("/api/quality/failure-modes"),
  });
}

export function QualityOperatorScriptsPage() {
  const canEdit = useHubModuleAdmin("quality");
  const navigate = useNavigate();
  const list = useOperatorScripts();
  const rows = list.data?.operatorScripts ?? [];
  const columns = useMemo<HubDataTableColumn<OperatorScript>[]>(
    () => [
      {
        id: "name",
        header: "Operator script",
        overflow: "nowrap",
        headerClassName: "w-[22rem]",
        cellClassName: "!align-middle w-[22rem] whitespace-nowrap",
        sortValue: (row) => row.label,
        cell: (row) => row.label,
      },
      {
        id: "description",
        header: "Description",
        cellClassName: "!align-middle min-w-0",
        sortValue: (row) => row.steps,
        cell: (row) => {
          const text = row.steps.replace(/\s+/g, " ").trim();
          if (text.trim() === "") return "—";
          return (
            <span className="block truncate" title={text}>
              {text}
            </span>
          );
        },
      },
      {
        id: "answers",
        header: "Answers",
        align: "right",
        overflow: "nowrap",
        headerClassName: "w-24",
        cellClassName: "!align-middle w-24",
        sortValue: (row) => answerCount(row),
        cell: (row) => answerCount(row),
      },
      {
        id: "prompts",
        header: "Prompts",
        align: "right",
        overflow: "nowrap",
        headerClassName: "w-24",
        cellClassName: "!align-middle w-24",
        sortValue: (row) => stepLineCount(row.steps),
        cell: (row) => stepLineCount(row.steps),
      },
      {
        id: "edit",
        header: "",
        overflow: "nowrap",
        headerClassName: "w-20",
        cellClassName: "!align-middle w-20",
        sortValue: () => "",
        cell: (row) => (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            title="Edit"
            onClick={() => navigate(`/quality/settings/operator-scripts/${row.id}`)}
          >
            Edit
          </Button>
        ),
      },
    ],
    [navigate],
  );

  return (
    <HubWorkspacePage>
      <HubWorkspaceHeader className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp
          breadcrumbLabel="Operator scripts"
          hoverHint="Prompts shown to the operator for immediate troubleshooting. One prompt per line."
        >
          Operator scripts
        </PageTitleWithHelp>
        {canEdit ? (
          <Button type="button" onClick={() => navigate("/quality/settings/operator-scripts/new")}>
            Add new operator script
          </Button>
        ) : null}
      </HubWorkspaceHeader>
      <HubWorkspaceBody>
        <HubDataTable
          fillViewport={false}
          className="h-auto w-full max-w-full self-start !max-h-none"
          tableClassName="w-full table-fixed"
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={list.isLoading}
          error={list.error instanceof Error ? list.error.message : null}
          empty="No operator scripts."
          defaultSortKey="name"
          defaultSortDir="asc"
        />
      </HubWorkspaceBody>
    </HubWorkspacePage>
  );
}

export function QualityOperatorScriptEditPage() {
  const { scriptId } = useParams();
  const isNew = scriptId == null;
  const canEdit = useHubModuleAdmin("quality");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const list = useOperatorScripts();
  const existing = list.data?.operatorScripts.find((script) => String(script.id) === scriptId);
  const savedAnswers = useMemo(() => answersFromScript(isNew ? undefined : existing), [existing, isNew]);
  const [answers, setAnswers] = useState<AnswerDraft[] | null>(null);
  const [committedField, setCommittedField] = useState<string | null>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const answerKey = useRef(1);
  const answerValue = answers ?? savedAnswers;
  const savedName = isNew ? "" : existing?.label ?? "";
  const savedSteps = isNew ? "" : existing?.steps ?? "";
  const savedQuestion = isNew ? "" : existing?.question ?? "";
  const pageLabel = isNew
    ? "New operator script"
    : existing == null
      ? "Operator script"
      : `Edit ${savedName}`;
  const save = useMutation({
    mutationFn: async (draft: { name: string; steps: string; question: string; answers: AnswerDraft[] }) => {
      const trimmedName = draft.name.trim();
      const options = draft.answers.map((answer) => ({
        label: answer.label.trim() === "" ? "Answer" : answer.label.trim(),
        outcome: answer.outcome,
        followUp: NEXT_PAGE_BY_OUTCOME[answer.outcome],
      }));
      if (!options.some((answer) => answer.outcome === "tier1")) {
        throw new ApiError(400, "One answer must send the device to Tier 1 review.");
      }
      if (!trimmedName) throw new ApiError(400, "Operator script name is required.");
      if (isNew) {
        const created = await apiFetch<{ id: number }>("/api/quality/operator-scripts", {
          method: "POST",
          body: { name: trimmedName },
        });
        await apiFetch(`/api/quality/operator-scripts/${created.id}`, {
          method: "PATCH",
          body: { name: trimmedName, steps: draft.steps, question: draft.question, options },
        });
        return created.id;
      }
      await apiFetch(`/api/quality/operator-scripts/${scriptId}`, {
        method: "PATCH",
        body: { name: trimmedName, steps: draft.steps, question: draft.question, options },
      });
      return Number(scriptId);
    },
    onSuccess: (id) => {
      setAnswers(null);
      void queryClient.invalidateQueries({ queryKey: ["quality", "failure-modes"] });
      if (isNew) navigate(`/quality/settings/operator-scripts/${id}`, { replace: true });
    },
    onError: (error: unknown) => {
      showToast({ tone: "error", message: error instanceof ApiError ? error.message : "Could not save the operator script." });
    },
  });

  const remove = useMutation({
    mutationFn: () => apiFetch(`/api/quality/operator-scripts/${scriptId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["quality", "failure-modes"] });
      navigate("/quality/settings/operator-scripts");
    },
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "This operator script is linked to a failure mode and cannot be deleted.",
      });
    },
  });

  function fieldFeedback(fieldId: string): {
    pending: boolean;
    commitSucceeded: boolean;
    commitError: string | null;
  } {
    const active = committedField === fieldId;
    return {
      pending: save.isPending && active,
      commitSucceeded: save.isSuccess && active,
      commitError: active && save.error instanceof Error ? save.error.message : null,
    };
  }

  function persist(
    partial: { name?: string; steps?: string; question?: string; answers?: AnswerDraft[] },
    fieldId: string,
  ) {
    setCommittedField(fieldId);
    const nextAnswers = partial.answers ?? answerValue;
    if (partial.answers != null) setAnswers(nextAnswers);
    save.mutate({
      name: partial.name ?? savedName,
      steps: partial.steps ?? savedSteps,
      question: partial.question ?? savedQuestion,
      answers: nextAnswers,
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp
          breadcrumbLabel={pageLabel}
          hoverHint="Steps, then the question, then one row per answer. Drag a row to reorder it. The next page is the action you pick. One Tier 1 answer stays locked. Other answers can also be Tier 1."
        >
          {pageLabel}
        </PageTitleWithHelp>
        {canEdit && !isNew ? (
          <Button type="button" variant="secondary" onClick={() => remove.mutate()} disabled={remove.isPending}>
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Delete
          </Button>
        ) : null}
      </div>
      {!isNew && existing == null ? (
        <p className="text-sm text-slate-600">
          {list.isError
            ? "Could not load this operator script."
            : list.isSuccess
              ? "This operator script was not found."
              : "Loading operator script."}
        </p>
      ) : (
      <Card>
      <CardBody className="space-y-4">
      <DeferredCommitTextInput
        label="Name"
        value={savedName}
        disabled={!canEdit}
        pending={fieldFeedback("name").pending}
        commitError={fieldFeedback("name").commitError}
        commitSucceeded={fieldFeedback("name").commitSucceeded}
        onCommit={(value) => persist({ name: value ?? "" }, "name")}
      />
      <div className="space-y-2">
        <p className="text-sm font-medium text-slate-800">Steps</p>
        <OperatorScriptStepsEditor
          value={savedSteps}
          disabled={!canEdit}
          scriptId={isNew ? undefined : scriptId}
          onCommit={(steps) => persist({ steps }, "steps")}
          onUploadError={(message) => showToast({ tone: "error", message })}
        />
      </div>
      <div className="border-t border-slate-200 pt-4">
      <DeferredCommitTextInput
        label="Question"
        value={savedQuestion}
        disabled={!canEdit}
        pending={fieldFeedback("question").pending}
        commitError={fieldFeedback("question").commitError}
        commitSucceeded={fieldFeedback("question").commitSucceeded}
        onCommit={(value) => persist({ question: value ?? "" }, "question")}
      />
      </div>
      <div className="space-y-2">
        <p className="text-sm font-medium text-slate-800">Answers</p>
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs font-medium text-slate-600">
              <tr>
                <th className="w-10 px-2 py-2" scope="col">
                  <span className="sr-only">Reorder</span>
                </th>
                <th className="px-3 py-2" scope="col">Answer</th>
                <th className="px-3 py-2" scope="col">Next page</th>
                <th className="w-10 px-2 py-2" scope="col">
                  <span className="sr-only">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {answerValue.map((answer, index) => {
                const tier1Locked = answer.lockedTier1;
                return (
                  <tr
                    key={answer.key}
                    className="border-t border-slate-200 hover:bg-slate-50/80"
                    onDragOver={(event) => {
                      if (dragKey == null) return;
                      event.preventDefault();
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      const fromKey = dragKey ?? event.dataTransfer.getData("text/plain");
                      if (fromKey === "" || fromKey === answer.key) return;
                      const next = [...answerValue];
                      const fromIndex = next.findIndex((row) => row.key === fromKey);
                      const toIndex = next.findIndex((row) => row.key === answer.key);
                      if (fromIndex < 0 || toIndex < 0) return;
                      const [moved] = next.splice(fromIndex, 1);
                      if (moved == null) return;
                      next.splice(toIndex, 0, moved);
                      setDragKey(null);
                      persist({ answers: next }, "answers");
                    }}
                  >
                    <td className="px-2 py-2 align-middle">
                      <button
                        type="button"
                        draggable={canEdit}
                        disabled={!canEdit}
                        data-hub-skip-press-animation=""
                        aria-label={`Move answer ${index + 1}`}
                        className="inline-flex h-8 w-8 cursor-grab items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50"
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", answer.key);
                          setDragKey(answer.key);
                        }}
                        onDragEnd={() => setDragKey(null)}
                      >
                        <GripVertical className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <DeferredCommitTextInput
                        value={answer.label}
                        disabled={!canEdit}
                        pending={fieldFeedback(`answer:${answer.key}`).pending}
                        commitError={fieldFeedback(`answer:${answer.key}`).commitError}
                        commitSucceeded={fieldFeedback(`answer:${answer.key}`).commitSucceeded}
                        onCommit={(value) =>
                          persist(
                            {
                              answers: answerValue.map((row) =>
                                row.key === answer.key ? { ...row, label: value ?? "" } : row,
                              ),
                            },
                            `answer:${answer.key}`,
                          )
                        }
                      />
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <ReadOnlyHoverLayer
                        active={answer.lockedTier1}
                        hoverHint="This Tier 1 answer stays on every script and cannot be changed."
                        layout="block"
                      >
                      <select
                        aria-label={`Next page for answer ${index + 1}`}
                        className="h-9 w-full min-w-72 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100"
                        value={answer.outcome}
                        disabled={!canEdit || answer.lockedTier1}
                        onChange={(event) => {
                          const outcome = ANSWER_OUTCOMES.find((value) => value === event.target.value);
                          if (outcome == null || answer.lockedTier1) return;
                          persist(
                            {
                              answers: answerValue.map((row) =>
                                row.key === answer.key
                                  ? { ...row, outcome, followUp: NEXT_PAGE_BY_OUTCOME[outcome] }
                                  : row,
                              ),
                            },
                            `answer:${answer.key}`,
                          );
                        }}
                      >
                        {(answer.lockedTier1 ? (["tier1"] as const) : ANSWER_OUTCOMES).map((outcome) => (
                          <option key={outcome} value={outcome}>
                            {answerActionLabel(outcome)}
                          </option>
                        ))}
                      </select>
                      </ReadOnlyHoverLayer>
                    </td>
                    <td className="px-2 py-2 align-middle">
                      {canEdit ? (
                        <ReadOnlyHoverLayer
                          active={tier1Locked}
                          hoverHint="This Tier 1 answer stays on every script. Other answers can also be Tier 1."
                          layout="inline"
                        >
                          <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-500"
                            aria-label={`Remove answer ${index + 1}`}
                            disabled={tier1Locked}
                            onClick={() =>
                              persist(
                                { answers: answerValue.filter((row) => row.key !== answer.key) },
                                "answers",
                              )
                            }
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </ReadOnlyHoverLayer>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {canEdit ? (
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              answerKey.current += 1;
              persist(
                {
                  answers: [...answerValue, answerDraft(`added-${answerKey.current}`, "", "repair")],
                },
                "answers",
              );
            }}
          >
            Add answer
          </Button>
        ) : null}
      </div>
      </CardBody>
      </Card>
      )}
    </div>
  );
}
